import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import UrlApi from "src/utils/Url";
import { quietPost } from "./api";

// Which country (and product) a deep screen belongs to.
//
// These screens used to rely on router state for their names, so opened from
// a link, a bookmark or a reload they said "Country" and "Application form
// questions" with no hint of which one. Router state is still used when it
// is there (no request); otherwise the names are read from the API.

let overviewPromise = null;
const profilePromises = {};

const overview = (dispatch) => {
  if (!overviewPromise) {
    overviewPromise = quietPost({ route: UrlApi.country.overview, dispatch }).then((res) => {
      if (!Array.isArray(res?.data)) overviewPromise = null; // try again next time
      return Array.isArray(res?.data) ? res.data : [];
    });
  }
  return overviewPromise;
};

const profile = (id, dispatch) => {
  if (!profilePromises[id]) {
    profilePromises[id] = quietPost({ route: UrlApi.country.profile, data: { id }, dispatch }).then((res) => {
      if (!res?.data?.country) delete profilePromises[id];
      return res?.data ?? null;
    });
  }
  return profilePromises[id];
};

/** The country `{ id, name, ... }` for a screen keyed by country id. */
export function useCountry(countryId, state) {
  const dispatch = useDispatch();
  const fromState = state?.name && (!state?.id || String(state.id) === String(countryId)) ? state : null;
  const [country, setCountry] = useState(fromState);
  useEffect(() => {
    if (fromState || !countryId || String(countryId) === "0") return undefined;
    let live = true;
    overview(dispatch).then((list) => {
      const found = list.find((entry) => String(entry.id) === String(countryId));
      if (live && found) setCountry(found);
    });
    return () => {
      live = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [countryId]);
  return country;
}

/**
 * Every country in the catalogue, A–Z: { loading, failed, countries }. The
 * same cached read the deep screens use for their names.
 */
export function useCountries() {
  const dispatch = useDispatch();
  const [state, setState] = useState({ loading: true, failed: false, countries: [] });
  useEffect(() => {
    let live = true;
    overview(dispatch).then((list) => {
      if (!live) return;
      const countries = [...list].sort((a, b) => String(a.name ?? "").localeCompare(String(b.name ?? "")));
      setState({ loading: false, failed: countries.length === 0, countries });
    });
    return () => {
      live = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return state;
}

/**
 * `{ product, country }` for a screen keyed by price (product) id. A price
 * is found by reading the profile of each country until one lists it; the
 * catalogue has a handful of countries and the reads are cached.
 */
export function useProduct(priceId, state) {
  const dispatch = useDispatch();
  const fromState = state?.title && String(state?.id) === String(priceId) ? state : null;
  const [found, setFound] = useState({ product: fromState, country: null });
  useEffect(() => {
    if (!priceId) return undefined;
    let live = true;
    (async () => {
      const countries = await overview(dispatch);
      // The country the screen was opened from, if any, is the likely one.
      const hinted = state?.name ? countries.filter((c) => c.name === state.name) : [];
      const order = [...hinted, ...countries.filter((c) => !hinted.includes(c))];
      for (const country of order) {
        // eslint-disable-next-line no-await-in-loop
        const data = await profile(country.id, dispatch);
        const price = data?.prices?.find((entry) => String(entry.id) === String(priceId));
        if (price) {
          if (live) setFound({ product: price, country: data.country ?? country });
          return;
        }
      }
    })();
    return () => {
      live = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [priceId]);
  return found;
}

/** "Schengen Visa · Lebanese" */
export const productLabel = (product) =>
  product ? [product.title, product.nationality_name || product.group_name].filter(Boolean).join(" · ") : "";

let groupsPromise = null;

/** The group `{ id, group_name, ... }` for a screen keyed by group id. */
export function useGroup(groupId, state) {
  const dispatch = useDispatch();
  const fromState = state?.group_name && String(state?.id) === String(groupId) ? state : null;
  const [group, setGroup] = useState(fromState);
  useEffect(() => {
    if (fromState || !groupId || String(groupId) === "0") return undefined;
    let live = true;
    if (!groupsPromise) {
      groupsPromise = quietPost({ route: UrlApi.groups.get, dispatch }).then((res) => {
        if (!Array.isArray(res?.data)) groupsPromise = null;
        return Array.isArray(res?.data) ? res.data : [];
      });
    }
    groupsPromise.then((list) => {
      const found = list.find((entry) => String(entry.id) === String(groupId));
      if (live && found) setGroup(found);
    });
    return () => {
      live = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupId]);
  return group;
}

/** Forget cached reads after an edit that changes them. */
export const invalidateCatalogue = () => {
  overviewPromise = null;
  groupsPromise = null;
  Object.keys(profilePromises).forEach((key) => delete profilePromises[key]);
};

/**
 * Every product in the catalogue, grouped by country:
 * [{ country, products: [price rows] }], countries A–Z. A group's products
 * are listed once, under the first of its countries. Reads fresh each time
 * `when` turns true (the product wizard opening), so a product made a
 * minute ago is offered too.
 */
export function useAllProducts(when) {
  const dispatch = useDispatch();
  const [state, setState] = useState({ loading: true, failed: false, byCountry: [] });
  useEffect(() => {
    if (!when) return undefined;
    let live = true;
    invalidateCatalogue();
    setState({ loading: true, failed: false, byCountry: [] });
    (async () => {
      const countries = await overview(dispatch);
      if (!countries.length) return live && setState({ loading: false, failed: true, byCountry: [] });
      const profiles = await Promise.all(countries.map((country) => profile(country.id, dispatch)));
      const seen = new Set();
      const byCountry = profiles
        .map((data, index) => {
          const country = data?.country ?? countries[index];
          const products = (data?.prices ?? []).filter((price) => !seen.has(price.id) && seen.add(price.id));
          return { country, products };
        })
        .filter((entry) => entry.products.length)
        .sort((a, b) => String(a.country.name).localeCompare(String(b.country.name)));
      if (live) setState({ loading: false, failed: false, byCountry });
      return null;
    })();
    return () => {
      live = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [when]);
  return state;
}

/**
 * Every country's profile, for screens keyed by an id the API cannot name
 * by itself (a visa type on a country, a nationality on that visa type).
 * Cached for the session like the rest: { loading, failed, profiles }.
 */
export function useProfiles() {
  const dispatch = useDispatch();
  const [state, setState] = useState({ loading: true, failed: false, profiles: [] });
  useEffect(() => {
    let live = true;
    (async () => {
      const countries = await overview(dispatch);
      if (!countries.length) return live && setState({ loading: false, failed: true, profiles: [] });
      const profiles = await Promise.all(countries.map((country) => profile(country.id, dispatch)));
      if (live) setState({ loading: false, failed: false, profiles: profiles.filter(Boolean) });
      return null;
    })();
    return () => {
      live = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return state;
}

/**
 * Where a country's visa type (country_visa_type id) sits:
 * { country, visaType: { country_visa_type_id, type_id, type_name, nationalities } }.
 */
export function useCountryVisaType(countryVisaTypeId) {
  const { loading, profiles } = useProfiles();
  for (const data of profiles) {
    const visaType = (data.visa_types ?? []).find((row) => String(row.country_visa_type_id) === String(countryVisaTypeId));
    if (visaType) return { loading, country: data.country, visaType, prices: data.prices ?? [] };
  }
  return { loading, country: null, visaType: null, prices: [] };
}

/**
 * Where one nationality of a country's visa type (nationality_country id)
 * sits: { country, visaType, nationality }.
 */
export function useNationalityLink(nationalityCountryId) {
  const { loading, profiles } = useProfiles();
  for (const data of profiles) {
    for (const visaType of data.visa_types ?? []) {
      const nationality = (visaType.nationalities ?? []).find((row) => String(row.id) === String(nationalityCountryId));
      if (nationality) return { loading, country: data.country, visaType, nationality };
    }
  }
  return { loading, country: null, visaType: null, nationality: null };
}
